import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import ResponsiveTable from 'components/shared/tables/responsive/ResponsiveTable';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import ClientFilters from 'components/client/ClientFilters';
import ClientStatusZones from 'components/client/ClientStatusZones';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { clientListBasicFields } from 'common/constants/query-fields/client';
import routes from 'common/routes';
import { getClientColumns } from 'common/table/columns';
import { getClientData } from 'common/table/data';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import { canModify } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import clientSlice from 'store/modules/client';

function ClientList() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'routes', 'tables']);
  const pageTitle = t('client.list', { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const clients = useSelector((state) => state.client.data.clients);
  const filters = useSelector((state) => state.client.filters);
  const pagination = useSelector((state) => state.client.pagination);
  const paginatorInfo = useSelector((state) => state.client.paginatorInfo);
  const refresh = useSelector((state) => state.client.refresh);
  const role = useSelector((state) => state.auth.user.role);
  const status = useSelector((state) => state.client.status.general);
  const counters = useSelector((state) => state.client.counters);

  const canModifyData = canModify(role);

  // Zona activa: se lee del propio filtro para que las pastillas y el desplegable de arriba nunca
  // se contradigan. Solo se considera "una zona" cuando hay exactamente un estado marcado; con
  // varios a la vez no hay una zona que destacar.
  const activeZone = filters.membership_status?.length === 1 ? filters.membership_status[0] : null;

  const onZoneChange = (zone) => {
    // `updateFilters` REEMPLAZA el objeto entero, no hace merge: hay que reenviar el resto de
    // filtros o se pierden `role: CLIENT`, el plan, el género y el filtro de eliminados, y la
    // consulta pasaría a traer cualquier usuario.
    dispatch(clientSlice.actions.updateFilters({ ...filters, membership_status: zone ? [zone] : [] }));
    // Al cambiar de zona hay que volver a la página 1: si estabas en la 3 de "Activos" y pasas a
    // "En mora", que solo tiene una, la lista salía vacía y parecía que no había nadie en mora.
    dispatch(clientSlice.actions.updatePagination({ page: 1 }));
  };

  const appBarButton = useMemo(
    () =>
      canModifyData ? (
        <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createClient.path} />
      ) : null,
    [canModifyData]
  );

  const listClients = useCallback(() => {
    dispatch(
      clientSlice.actions.list({
        fields: clientListBasicFields,
        filters,
        pagination
      })
    );
  }, [clientListBasicFields, dispatch, filters, pagination]);

  useEffect(() => {
    listClients();
  }, [listClients, refresh]);

  // Los contadores de las zonas: se piden una vez al entrar, igual que en el panel de inicio.
  useEffect(() => {
    dispatch(clientSlice.actions.membershipStatusCounters());
  }, [dispatch]);

  useEffect(() => {
    setAppBarButton(appBarButton);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const tableData = getClientData(clients, t);
  const columns = getClientColumns({ data: tableData, role, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(clientSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(clientSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(clientSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    onChangePage,
    page: pagination.page - 1,
    rowsPerPage: pagination.first,
    selectableRows: canModifyData ? 'multiple' : 'none',
    selectableRowsHeader: canModifyData,
    textLabels: tableLocalization(pageTitle.toLowerCase(), t)
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <ClientFilters />
      <ClientStatusZones active={activeZone} counters={counters} onChange={onZoneChange} />
      <Box
        sx={{
          position: 'relative'
        }}>
        <LoadingBlock
          emptyBlockProps={{ message: emptyMessage }}
          isValid={clients.length > 0}
          skeletonProps={{ rows: 5 }}
          status={status}
          type="table">
          <ResponsiveTable
            className="user user--client"
            columns={columns}
            data={tableData}
            options={options}
            cardConfig={{ titleColumn: 'user', summaryColumns: ['plan', 'membership_days_left'] }}
          />
        </LoadingBlock>
      </Box>
    </>
  );
}

export default ClientList;
